import ShadowContainerHorizontal from '@atoms/ShadowContainerHorizontal/ShadowContainerHorizontal';
import Tag from '@atoms/Tag/Tag';
import { ICrossSellItemsCTAStatuses } from '@models/ICrossSellItemsCTAStatuses';
import { ICrossSellTabbedProductsList } from '@models/ICrossSellTabbedProductsList';
import ITabbedProductListingItem from '@models/ITabbedProductListingItem';
import CrossSellProductCTAStatusEnum from '@models/enums/CrossSellProductCTAStatusEnum';
import TagTypesEnum from '@models/enums/TagTypesEnum';
import CrossSellProductCard from '@molecules/CrossSellProductCard/CrossSellProductCard';
import NavigationCarousel from '@molecules/NavigationCarousel/NavigationCarousel';
import slugify from '@utils/helpers/slugify';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { SwiperSlide } from 'swiper/react';
import { SwiperOptions } from 'swiper/types';

interface Props {
  readonly itemsPayload: ICrossSellTabbedProductsList;
  readonly isLightMode?: boolean;
  readonly backgroundColour?: string;
  readonly shadowColour?: string;
  readonly titleClassName?: string;
  readonly tabsClassName?: string;
  readonly cardClassName?: string;
  readonly defaultTabType?: TagTypesEnum;
  readonly carouselHorizontalSpacingClassName?: string;
  readonly additionalBreakpoints?: { [width: number]: SwiperOptions };
  readonly onAddItemToCart: (
    onSuccessfulAddToCart: (
      tabbedProductsList: ICrossSellTabbedProductsList
    ) => void
  ) => (code: string) => void;
  readonly onRemoveItemFromCart: (
    onSuccessfulRemoveFromCart: () => void
  ) => (code: string) => void;
}

const TabbedCrossSellProductListing = ({
  itemsPayload: { title, addToBasketLabel, addedLabel, removeLabel, tabs },
  isLightMode,
  backgroundColour = 'bg-grey-100',
  shadowColour = 'from-grey-100',
  titleClassName,
  tabsClassName,
  cardClassName,
  defaultTabType = TagTypesEnum.Three,
  carouselHorizontalSpacingClassName = '',
  additionalBreakpoints = {},
  onAddItemToCart,
  onRemoveItemFromCart,
}: Props) => {
  const isMobile = useMediaQuery(768);
  const isInitialRender = useRef(true);
  const firstSlideRef = useRef<HTMLDivElement>(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const tabTitles = tabs?.map((tab) => tab.title) || [];

  const getItemsCTAStatuses = (currentTabs?: ITabbedProductListingItem[]) =>
    (currentTabs || []).reduce((tabsAcc, currentTab, tabIndex) => {
      const currentTabItems = (currentTab?.items || []).reduce<
        Record<number, CrossSellProductCTAStatusEnum>
      >(
        (tabItemsAcc, item, tabItemIndex) => ({
          ...tabItemsAcc,
          ...{
            [`${tabItemIndex}`]: item.inCart
              ? CrossSellProductCTAStatusEnum.Remove
              : CrossSellProductCTAStatusEnum.AddToBasket,
          },
        }),
        {}
      );

      return {
        ...tabsAcc,
        ...{
          [`${tabIndex}`]: currentTabItems,
        },
      };
    }, {});

  const [itemsCTAStatuses, setItemsCTAStatuses] =
    useState<ICrossSellItemsCTAStatuses>(getItemsCTAStatuses(tabs));

  useEffect(() => {
    setItemsCTAStatuses(getItemsCTAStatuses(tabs));
  }, [tabs]);

  useEffect(() => {
    // reset the active tab index when new tab data is passed to the component to prevent no tab being selected
    if (!tabs?.[activeTabIndex]?.items) setActiveTabIndex(0);
  }, [activeTabIndex, tabs]);

  useEffect(() => {
    // We want to avoid focusing the first slide on mount as it will break the focus
    // when this component is used on a regular page (e.g. Basket page)
    if (firstSlideRef.current && !isInitialRender.current) {
      firstSlideRef.current.focus({ preventScroll: true });
    }

    if (isInitialRender.current) isInitialRender.current = false;
  }, [activeTabIndex]);

  const handleUpdateItemCTAStatus = (
    index: number,
    newCtaStatus: CrossSellProductCTAStatusEnum
  ) => {
    setItemsCTAStatuses((previousState) => ({
      ...previousState,
      [`${activeTabIndex}`]: {
        ...previousState[activeTabIndex],
        [`${index}`]: newCtaStatus,
      },
    }));
  };

  const handleSuccessfulAddItemToCart =
    (index: number) => (tabbedProductsList: ICrossSellTabbedProductsList) => {
      // Fallback to first tab if the items at activeIndexTab is undefined
      if (
        tabbedProductsList?.tabs &&
        tabs &&
        tabs.length !== tabbedProductsList.tabs.length
      ) {
        setActiveTabIndex(0);
      }
      handleUpdateItemCTAStatus(index, CrossSellProductCTAStatusEnum.Added);
      setTimeout(() => {
        handleUpdateItemCTAStatus(index, CrossSellProductCTAStatusEnum.Remove);
      }, 2000);
    };

  const handleSuccessfulRemoveItemFromCart = (index: number) => () =>
    handleUpdateItemCTAStatus(index, CrossSellProductCTAStatusEnum.AddToBasket);

  const tabsContainer = (
    <ShadowContainerHorizontal threshold={0.01} shadowColour={shadowColour}>
      <div className={classNames('flex gap-x-5', tabsClassName)}>
        {tabTitles.map((tabTitle, index) => {
          const isActive = index === activeTabIndex;

          return (
            <Tag
              key={tabTitle}
              text={tabTitle}
              tagType={isActive ? TagTypesEnum.Secondary : defaultTabType}
              pointerStates={!isActive}
              disabled={isActive}
              onClick={() => setActiveTabIndex(index)}
              isLightMode={isLightMode}
            />
          );
        })}
      </div>
    </ShadowContainerHorizontal>
  );

  return (
    <section
      className={classNames('text-black-100', backgroundColour)}
      data-testid="Tabbed-Cross-Sell-Product-Listing"
    >
      {title && (
        <p
          className={classNames('h6 mb-5 md:mb-7.5', titleClassName)}
          data-testid="Tabbed-Cross-Sell-Product-Listing-Title"
        >
          {title}
        </p>
      )}

      <div
        className="mb-7.5 md:hidden"
        data-testid="Tabbed-Cross-Sell-Product-Listing-Mobile-Tabs"
      >
        {tabsContainer}
      </div>

      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{
            opacity: 0,
            transition: {
              duration: 0,
            },
          }}
          key={`${tabTitles[activeTabIndex]}-${activeTabIndex}`}
          data-testid="Tabbed-Cross-Sell-Product-Listing-Carousel-Wrapper"
        >
          <NavigationCarousel
            id="cross-sell-product-carousel"
            items={tabs?.[activeTabIndex]?.items || []}
            loop={false}
            shadows
            shadowColour={shadowColour}
            slidesToShowTablet={3}
            title={tabsContainer}
            titleClassName="min-w-0 hidden md:block"
            titleWrapperClassName="md:gap-x-10"
            hideHeaderOnMobile
            horizontalSpacingClassName={carouselHorizontalSpacingClassName}
            desktopBreakpoint="md"
            spaceBetween={isMobile ? 20 : 16}
            shouldReinitializeNavigation
            additionalBreakpoints={{
              375: {
                slidesPerView: 1.3,
              },
              480: {
                slidesPerView: 1.5,
              },
              565: {
                slidesPerView: 2.1,
              },
              ...additionalBreakpoints,
            }}
            isLightMode={isLightMode}
          >
            {(itemsToDisplay) =>
              itemsToDisplay.map((item, index) => (
                <SwiperSlide
                  key={slugify(`${item.name}-${index}`)}
                  className="stretch-card"
                >
                  <div tabIndex={-1} ref={index === 0 ? firstSlideRef : null}>
                    <CrossSellProductCard
                      {...item}
                      ctaStatus={itemsCTAStatuses[activeTabIndex]![index]!}
                      addToBasketLabel={addToBasketLabel}
                      addedLabel={addedLabel}
                      removeLabel={removeLabel}
                      onAddToCart={onAddItemToCart(
                        handleSuccessfulAddItemToCart(index)
                      )}
                      onRemoveFromCart={onRemoveItemFromCart(
                        handleSuccessfulRemoveItemFromCart(index)
                      )}
                      isLightMode={isLightMode}
                      dataTestId={`Cross-Sell-Product-Card-Tab-${activeTabIndex}-Item-${index}`}
                      className={cardClassName}
                    />
                  </div>
                </SwiperSlide>
              ))
            }
          </NavigationCarousel>
        </motion.div>
      </AnimatePresence>
    </section>
  );
};

export default TabbedCrossSellProductListing;
